import React, { useCallback, useEffect, useRef, useState } from 'react';
import { formatDateTime } from '@/utils/displayDateTime';
import { ClampText } from '@/components/ui';
import { Link, useParams } from 'react-router-dom';
import { Alert, Button, DataTable, EmptyValue, Form, ListPage, Modal, PageHeader, RowActions, Spinner, StatusBadge } from '../../../components/ui';
import type { DataTableColumn } from '../../../components/ui';
import { useToast } from '../../../contexts/ToastContext';
import type { StatusVariant } from '../../../utils/statusUtils';
import { materialCountService, type CountDetail, type CountLine, type CountLineState } from '../../../services/materialCountService';
import MaterialsSubnav from '../components/MaterialsSubnav';
import { formatQuantity } from '../utils/adjustInventory';
import { COUNTS_PATH, ledgerPath, locationKindLabel } from '../utils/siteDrillThrough';

const STATE: Record<CountLineState, { label: string; variant: StatusVariant }> = {
  'not-counted': { label: 'Not counted', variant: 'secondary' },
  ready: { label: 'Ready to post', variant: 'info' },
  'awaiting-sign-off': { label: 'Awaiting sign-off', variant: 'danger' },
  investigating: { label: 'Investigating', variant: 'orange' },
  posted: { label: 'Posted', variant: 'success' },
};

const PCT = new Intl.NumberFormat('en-CA', { maximumFractionDigits: 1, signDisplay: 'exceptZero' });

type Load = { status: 'loading' } | { status: 'error'; message: string } | { status: 'ready'; count: CountDetail };

interface Row {
  line: CountLine;
  /** First line of its location: the location name is shown once per group. */
  first: boolean;
  /** Position among the editable inputs, so Enter can move to the next one. */
  inputIndex: number | null;
}

/**
 * The counted-quantity box. Saves when it loses focus or on Enter, and Enter moves to the next
 * bin's box, so a whole site can be keyed in from a survey report without touching the mouse.
 */
const CountedInput: React.FC<{
  line: CountLine;
  index: number;
  disabled: boolean;
  onSave: (line: CountLine, value: number | null) => Promise<void>;
}> = ({ line, index, disabled, onSave }) => {
  const [draft, setDraftState] = useState(line.counted === null ? '' : String(line.counted));
  // Enter moves focus, which blurs this box in the same tick as the last keystroke: the save reads
  // the ref, not the render's copy of the draft, or a fast typist's last digits would be lost.
  const draftRef = useRef(draft);
  const setDraft = (value: string) => {
    draftRef.current = value;
    setDraftState(value);
  };
  const [saving, setSaving] = useState(false);
  const focused = useRef(false);

  // Follow the server unless the user is typing in this box.
  useEffect(() => {
    if (!focused.current) setDraft(line.counted === null ? '' : String(line.counted));
  }, [line.counted]);

  const commit = async () => {
    const text = draftRef.current.trim();
    const value = text === '' ? null : Number(text);
    if (value !== null && !(value >= 0)) {
      setDraft(line.counted === null ? '' : String(line.counted));
      return;
    }
    if (value === line.counted) return;
    setSaving(true);
    try {
      await onSave(line, value);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="tw-flex tw-items-center tw-justify-end tw-gap-1.5">
      {saving ? <Spinner animation="border" size="sm" /> : null}
      <input
        type="number"
        inputMode="decimal"
        min={0}
        step="any"
        data-count-input={index}
        aria-label={`Counted at ${line.bin.locationName} · ${line.bin.code}`}
        className="tw-h-9 tw-w-28 tw-rounded-md tw-border tw-border-line tw-bg-surface tw-px-2 tw-text-right tw-tabular-nums tw-text-content focus:tw-border-line-focus focus:tw-shadow-focus focus:tw-outline-none disabled:tw-bg-surface-alt disabled:tw-text-content-muted"
        value={draft}
        disabled={disabled}
        onFocus={() => (focused.current = true)}
        onChange={(event) => setDraft(event.target.value)}
        onBlur={() => {
          focused.current = false;
          void commit();
        }}
        onKeyDown={(event) => {
          if (event.key !== 'Enter') return;
          event.preventDefault();
          const next = document.querySelector<HTMLInputElement>(`[data-count-input="${index + 1}"]`);
          if (next) next.focus();
          else (event.target as HTMLInputElement).blur();
        }}
      />
      <span className="tw-w-12 tw-truncate tw-text-left tw-text-xs tw-text-content-muted">{line.material.unitOfMeasure}</span>
    </div>
  );
};

/** One count sheet: every bin at the site, expected against counted (MT-K01..K03, K08). */
const CountSessionPage: React.FC = () => {
  const { countId = '' } = useParams<{ countId: string }>();
  const { showToast } = useToast();
  const [load, setLoad] = useState<Load>({ status: 'loading' });
  const [busy, setBusy] = useState<string | null>(null);
  /** The line being marked for investigation, and the note being written for it. */
  const [investigate, setInvestigate] = useState<{ line: CountLine; note: string } | null>(null);

  const refresh = useCallback(async () => {
    try {
      setLoad({ status: 'ready', count: await materialCountService.get(countId) });
    } catch (error) {
      setLoad({ status: 'error', message: error instanceof Error ? error.message : 'Failed to load the count' });
    }
  }, [countId]);

  useEffect(() => {
    setLoad({ status: 'loading' });
    void refresh();
  }, [refresh]);

  /** Runs a change and shows the count it returns; a refusal is a toast, and the sheet reloads. */
  const act = async (key: string, run: () => Promise<CountDetail | { posted: number; count: CountDetail }>, done?: (result: any) => string | null) => {
    setBusy(key);
    try {
      const result = await run();
      const count = 'lineList' in result ? result : result.count;
      setLoad({ status: 'ready', count });
      const message = done?.(result);
      if (message) showToast(message);
    } catch (error) {
      showToast(error instanceof Error ? error.message : 'Something went wrong', 'error');
      void refresh();
    } finally {
      setBusy(null);
    }
  };

  if (load.status === 'loading') {
    return (
      <ListPage>
        <PageHeader title="Count" />
        <div className="tw-flex tw-justify-center tw-py-10">
          <Spinner animation="border" />
        </div>
      </ListPage>
    );
  }
  if (load.status === 'error') {
    return (
      <ListPage>
        <PageHeader title="Count" />
        <MaterialsSubnav />
        <Alert variant="danger">
          <p className="tw-mb-2">{load.message}</p>
          <Link to={COUNTS_PATH}>Back to counts</Link>
        </Alert>
      </ListPage>
    );
  }

  const { count } = load;
  const open = count.status === 'OPEN';
  const siteName = count.site.number ? `${count.site.name} #${count.site.number}` : count.site.name;
  const ready = count.lineList.filter((line) => line.state === 'ready').length;
  const awaiting = count.lineList.filter((line) => line.state === 'awaiting-sign-off').length;

  let inputIndex = 0;
  const rows: Row[] = count.lineList.map((line, index) => {
    const editable = open && count.can.post && line.state !== 'posted';
    return {
      line,
      first: index === 0 || count.lineList[index - 1].bin.locationId !== line.bin.locationId,
      inputIndex: editable ? inputIndex++ : null,
    };
  });

  const saveCounted = async (line: CountLine, value: number | null) =>
    act(`save-${line.id}`, () => materialCountService.updateLine(count.id, line.id, { countedQuantity: value }));

  const postReason = (line: CountLine): string | null => {
    if (!open) return 'This count is closed';
    if (line.counted === null) return 'Enter what was counted first';
    if (line.outsideTolerance && !count.can.accept) return `More than ${count.tolerancePct}% out: needs sign-off from someone who can accept counts`;
    if (!line.outsideTolerance && !count.can.post) return 'You do not have permission to post counts';
    return null;
  };

  const columns: DataTableColumn<Row>[] = [
    {
      key: 'location',
      header: 'Location',
      mobile: 'subtitle',
      className: 'tw-min-w-[10rem]',
      cell: ({ line, first }) =>
        first ? (
          <div>
            <p className="tw-mb-0 tw-font-medium tw-text-content">{line.bin.locationName}</p>
            <p className="tw-mb-0 tw-text-xs tw-text-content-muted">{locationKindLabel(line.bin.locationType)}</p>
          </div>
        ) : null,
    },
    {
      key: 'bin',
      header: 'Bin',
      mobile: 'title',
      className: 'tw-whitespace-nowrap',
      cell: ({ line }) => <span className="tw-font-medium tw-text-content">{line.bin.code}</span>,
    },
    {
      key: 'material',
      header: 'Material',
      className: 'tw-min-w-[13rem]',
      cell: ({ line }) => (
        <ClampText title={line.material.label}>
          {line.material.label}
        </ClampText>
      ),
    },
    {
      key: 'expected',
      header: 'Expected',
      mobileLabel: 'Expected',
      align: 'right',
      className: 'tw-whitespace-nowrap tw-tabular-nums',
      cell: ({ line }) => (
        <span className={line.expected < 0 ? 'tw-font-semibold tw-text-danger' : 'tw-text-content-secondary'}>
          {formatQuantity(line.expected, line.material.unitOfMeasure)}
        </span>
      ),
    },
    {
      key: 'counted',
      header: 'Counted',
      mobileLabel: 'Counted',
      align: 'right',
      className: 'tw-whitespace-nowrap',
      cell: ({ line, inputIndex: index }) =>
        index === null ? (
          line.counted === null ? (
            <EmptyValue />
          ) : (
            <span className="tw-font-medium tw-tabular-nums tw-text-content">{formatQuantity(line.counted, line.material.unitOfMeasure)}</span>
          )
        ) : (
          <CountedInput line={line} index={index} disabled={busy === 'post-ready' || busy === 'close'} onSave={saveCounted} />
        ),
    },
    {
      key: 'variance',
      header: 'Variance',
      mobileLabel: 'Variance',
      align: 'right',
      className: 'tw-whitespace-nowrap tw-tabular-nums',
      cell: ({ line }) =>
        line.variance === null ? (
          <EmptyValue />
        ) : (
          <div>
            <p className={`tw-mb-0 ${line.outsideTolerance ? 'tw-font-semibold tw-text-danger' : 'tw-text-content'}`}>
              {line.variance > 0 ? '+' : ''}
              {formatQuantity(line.variance, line.material.unitOfMeasure)}
            </p>
            <p className={`tw-mb-0 tw-text-xs ${line.outsideTolerance ? 'tw-text-danger' : 'tw-text-content-muted'}`}>
              {line.variancePct === null ? (line.outsideTolerance ? 'none expected' : '') : `${PCT.format(line.variancePct)}%`}
            </p>
          </div>
        ),
    },
    {
      key: 'status',
      header: 'Status',
      mobile: 'trailing',
      cell: ({ line }) => (
        <div className="tw-flex tw-flex-col tw-items-start tw-gap-0.5">
          <StatusBadge
            status={line.state}
            variant={STATE[line.state].variant}
            label={line.posted?.acceptedOutsideTolerance ? 'Accepted' : STATE[line.state].label}
            soft
            pill
          />
          {line.posted ? (
            <span className="tw-text-xs tw-text-content-muted">{line.posted.by ?? 'Posted'}</span>
          ) : line.note ? (
            <ClampText className="tw-max-w-[12rem] tw-text-xs tw-text-content-muted" title={line.note}>
              {line.note}
            </ClampText>
          ) : null}
        </div>
      ),
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      width: '12rem',
      cell: ({ line }) => {
        if (line.state === 'posted') {
          return (
            <Link to={ledgerPath({ siteId: count.site.id, binId: line.bin.id })} className="tw-text-sm tw-text-primary hover:tw-underline">
              History
            </Link>
          );
        }
        const reason = postReason(line);
        return (
          <div className="tw-flex tw-items-center tw-justify-end tw-gap-1">
            <Button
              size="sm"
              variant={line.outsideTolerance ? 'outline-danger' : 'outline-primary'}
              disabled={reason !== null || busy !== null}
              disabledReason={reason ?? undefined}
              onClick={() =>
                void act(`post-${line.id}`, () => materialCountService.postLine(count.id, line.id), () =>
                  line.outsideTolerance ? `Accepted ${line.bin.code}: the bin now reads ${formatQuantity(line.counted!, line.material.unitOfMeasure)}` : null
                )
              }
            >
              {line.outsideTolerance ? 'Accept' : 'Post'}
            </Button>
            <RowActions
              actions={[
                {
                  kind: 'custom',
                  icon: line.state === 'investigating' ? 'bi-arrow-counterclockwise' : 'bi-search',
                  label: line.state === 'investigating' ? 'Stop investigating' : 'Investigate',
                  disabled: !open || !count.can.post,
                  onClick: () =>
                    line.state === 'investigating'
                      ? void act(`inv-${line.id}`, () => materialCountService.updateLine(count.id, line.id, { investigating: false }))
                      : setInvestigate({ line, note: line.note ?? '' }),
                },
              ]}
            />
          </div>
        );
      },
    },
  ];

  return (
    <ListPage>
      <PageHeader
        title={`Count · ${siteName}`}
        subtitle={`${count.methodLabel}, taken ${formatDateTime(count.countedAt)}${count.createdBy ? ` · started by ${count.createdBy}` : ''} · tolerance ${count.tolerancePct}%`}
        actions={[
          { key: 'refresh', label: 'Refresh', icon: 'bi-arrow-clockwise', intent: 'neutral', overflow: true, onClick: () => void refresh() },
          {
            key: 'close',
            label: open ? 'Close count' : 'Closed',
            icon: open ? 'bi-lock' : 'bi-lock-fill',
            intent: 'neutral',
            disabled: !open || !count.can.post || !!count.closeBlocker || busy !== null,
            title: !open ? `Closed ${count.closedAt ? formatDateTime(count.closedAt) : ''}` : count.closeBlocker ?? undefined,
            onClick: () => void act('close', () => materialCountService.close(count.id), () => 'Count closed'),
          },
          {
            key: 'post-ready',
            label: ready ? `Post ${ready} within tolerance` : 'Post within tolerance',
            icon: 'bi-check2-all',
            intent: 'primary',
            hidden: !open,
            disabled: !ready || !count.can.post || busy !== null,
            title: !count.can.post ? 'You do not have permission to post counts' : !ready ? 'No counted line within tolerance is waiting' : undefined,
            onClick: () =>
              void act('post-ready', () => materialCountService.postReady(count.id), (result) =>
                `Posted ${result.posted} ${result.posted === 1 ? 'line' : 'lines'}${awaiting ? `; ${awaiting} still ${awaiting === 1 ? 'needs' : 'need'} sign-off` : ''}`
              ),
          },
        ]}
      />

      <MaterialsSubnav />

      <div className="tw-mb-3 tw-flex tw-flex-wrap tw-items-center tw-gap-x-4 tw-gap-y-1 tw-text-sm tw-text-content-secondary" aria-live="polite">
        <span>
          <span className="tw-font-medium tw-tabular-nums tw-text-content">{count.lines.counted}</span> of {count.lines.total} bins counted
        </span>
        <span>
          <span className="tw-font-medium tw-tabular-nums tw-text-content">{count.lines.posted}</span> posted
        </span>
        {awaiting ? <span className="tw-font-medium tw-text-danger">{awaiting} awaiting sign-off</span> : null}
        {count.lines.investigating ? <span className="tw-font-medium tw-text-orange-700">{count.lines.investigating} investigating</span> : null}
        {open ? (
          <span className="tw-text-content-muted">
            Type each count and press Enter for the next bin. Within {count.tolerancePct}% posts together; anything further needs sign-off.
          </span>
        ) : (
          <StatusBadge status="closed" variant="secondary" label="Closed" soft pill />
        )}
        {count.note ? <span className="tw-text-content-muted">Note: {count.note}</span> : null}
      </div>

      <ListPage.Body>
        <DataTable fill columns={columns} data={rows} rowKey={({ line }) => line.id} emptyMessage="This count has no lines." />
      </ListPage.Body>

      <Modal show={investigate !== null} onHide={() => setInvestigate(null)}>
        <Modal.Header closeButton>
          <Modal.Title>Investigate {investigate ? `${investigate.line.bin.locationName} · ${investigate.line.bin.code}` : ''}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="tw-space-y-3">
          <p className="tw-mb-0 tw-text-sm tw-text-content-secondary">
            The line is set aside: nothing posts, and the count can be closed with it still open for follow-up.
          </p>
          <Form.Group controlId="count-investigate-note">
            <Form.Label>What needs looking into?</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              maxLength={1000}
              autoFocus
              value={investigate?.note ?? ''}
              placeholder="e.g. Shed door blocked, re-measure Monday"
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                setInvestigate((current) => (current ? { ...current, note: event.target.value } : current))
              }
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setInvestigate(null)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={!investigate?.note.trim()}
            disabledReason="Say what needs looking into"
            onClick={() => {
              if (!investigate) return;
              const { line, note } = investigate;
              setInvestigate(null);
              void act(`inv-${line.id}`, () => materialCountService.updateLine(count.id, line.id, { investigating: true, note }));
            }}
          >
            Mark for investigation
          </Button>
        </Modal.Footer>
      </Modal>
    </ListPage>
  );
};

export default CountSessionPage;
